import React from "react";
import { Modal, Button, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { ChatCompletionRequest, ToolCall } from "../../../model_gen/chat";
import RequestOverview from "../../Request/RequestOverview";
import { RequestsService } from "../../../services/RequestsService";

interface DatasetRequestEditProps {
  request: ChatCompletionRequest;
  onSave: (request: ChatCompletionRequest) => void;
  onSelect?: () => void;
  isEligible?: boolean;
  datasetId?: string;
  apiKey: string;
}

const DatasetRequestEdit: React.FC<DatasetRequestEditProps> = ({
  request,
  onSave,
  onSelect,
  isEligible = false,
  apiKey,
}) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [editedContent, setEditedContent] = React.useState<string | null>(null);
  const [initialContent, setInitialContent] = React.useState<string | null>(
    null
  );
  const [pendingRequest, setPendingRequest] =
    React.useState<ChatCompletionRequest | null>(null);
  const [editingMessageIndex, setEditingMessageIndex] = React.useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [editedToolCalls, setEditedToolCalls] = React.useState<
    ToolCall[] | null
  >(null);
  const [showAddToolCall, setShowAddToolCall] = React.useState(false);
  const [newToolCall, setNewToolCall] = React.useState({
    name: "",
    arguments: "",
  });

  const messages = request.params.messages || [];
  const lastMessage = messages[messages.length - 1];
  const hasAssistantMessage = lastMessage?.role === "assistant";

  const handleModalOpen = () => {
    // Initialize with either existing edited content or original content
    const datasetResponse = request.metadata?._maitai_dataset_request_response;
    const content =
      datasetResponse !== undefined && datasetResponse !== null
        ? (datasetResponse as string)
        : (lastMessage.content as string);
    setEditedContent(content);
    setInitialContent(content);
    setPendingRequest(request);
    const datasetToolCalls =
      request.metadata?._maitai_dataset_request_response_tool_calls;
    setEditedToolCalls(
      datasetToolCalls
        ? (datasetToolCalls as ToolCall[])
        : lastMessage.tool_calls || []
    );
    setIsModalVisible(true);
  };

  const handleMessageEdit = (index: number, content: string) => {
    setEditedContent(content);
    setPendingRequest(request);
  };

  const handleModelChange = (model: string) => {
    const newRequest = {
      ...request,
      params: {
        ...request.params,
        model,
      },
    };
    setPendingRequest(newRequest);
  };

  const handleSave = () => {
    if (!pendingRequest) {
      message.error("No request to save");
      return;
    }

    if (isEligible && (editedContent !== null || editedToolCalls !== null)) {
      // This request hasn't been added to the dataset yet
      // Only update content if it was edited
      const label = {
        role: "assistant",
        content: editedContent || lastMessage.content,
        tool_calls: editedToolCalls || lastMessage.tool_calls,
      };
      pendingRequest.metadata._maitai_annotation_label = JSON.stringify(label);
      onSave(pendingRequest);
      setEditedContent(null);
      setInitialContent(null);
      setPendingRequest(null);
      setEditedToolCalls(null);
      setIsModalVisible(false);

      if (isEligible && onSelect) {
        onSelect();
      }
      return;
    }

    // Update the request response via the API
    setIsLoading(true);

    const payload = {
      message_content: editedContent ?? lastMessage.content,
      tool_calls: editedToolCalls ?? lastMessage.tool_calls,
    };

    RequestsService.updateRequestResponse(
      pendingRequest.request_id,
      payload,
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        onSave(pendingRequest);
        setEditedContent(null);
        setInitialContent(null);
        setPendingRequest(null);
        setEditedToolCalls(null);
        setIsModalVisible(false);
        message.success("Message updated successfully");
      },
      (error: any) => {
        console.error("Failed to update message:", error);
        message.error("Failed to update message");
        setIsLoading(false);
      }
    );
  };

  const handleCancel = () => {
    setEditedContent(null);
    setInitialContent(null);
    setPendingRequest(null);
    setEditingMessageIndex(null);
    setEditedToolCalls(null);
    setShowAddToolCall(false);
    setIsModalVisible(false);
  };

  const handleDeleteToolCall = (toolCallIndex: number) => {
    if (!editedToolCalls) return;
    const updatedToolCalls = editedToolCalls.filter(
      (_, index) => index !== toolCallIndex
    );
    setEditedToolCalls(updatedToolCalls);
    setPendingRequest(request); // Ensure pendingRequest is set for save button
  };

  const hasChanges =
    editedContent !== initialContent ||
    JSON.stringify(editedToolCalls) !== JSON.stringify(lastMessage.tool_calls);

  const handleToolCallChange = (
    toolCallIndex: number,
    field: string,
    value: string
  ) => {
    if (!editedToolCalls) return;

    const updatedToolCalls = [...editedToolCalls];
    if (field === "name") {
      updatedToolCalls[toolCallIndex].function.name = value;
    } else if (field === "arguments") {
      updatedToolCalls[toolCallIndex].function.arguments = value;
    }

    setEditedToolCalls(updatedToolCalls);
  };

  const handleAddToolCall = () => {
    setShowAddToolCall(true);
  };

  const confirmAddToolCall = () => {
    if (!editedToolCalls) return;

    const newToolCallObj = {
      id: `call_${Date.now()}`,
      type: "function",
      function: {
        name: newToolCall.name,
        arguments: newToolCall.arguments,
      },
    };

    setEditedToolCalls([...editedToolCalls, newToolCallObj]);
    setNewToolCall({ name: "", arguments: "" });
    setShowAddToolCall(false);
  };

  return (
    <>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={handleModalOpen}
        size="small"
        disabled={!hasAssistantMessage}
        title={
          !hasAssistantMessage ? "No assistant message to edit" : "Edit request"
        }
      />

      <Modal
        title="Edit Request"
        open={isModalVisible}
        onCancel={handleCancel}
        width={1000}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={isLoading}
            disabled={!pendingRequest || (!hasChanges && !editedToolCalls)}>
            Save
          </Button>,
        ]}>
        <RequestOverview
          request={request}
          isEditable={true}
          onMessageEditComplete={handleMessageEdit}
          onModelChange={handleModelChange}
          editingMessageIndex={editingMessageIndex}
          onEditingMessageChange={setEditingMessageIndex}
          editMode="final-assistant"
          showDetails={false}
          expanded={true}
          onToolCallChange={handleToolCallChange}
          onAddToolCall={handleAddToolCall}
          onConfirmAddToolCall={confirmAddToolCall}
          onDeleteToolCall={handleDeleteToolCall}
          showAddToolCall={showAddToolCall}
          newToolCall={newToolCall}
          setNewToolCall={setNewToolCall}
          setShowAddToolCall={setShowAddToolCall}
        />
      </Modal>
    </>
  );
};

export default DatasetRequestEdit;
